import { ExpenseByVendorRow } from "@common/types/reports/types";

/**
 * Sort tokens accepted by the Expense by Vendor report.
 *
 * `VENDOR_ASC` / `VENDOR_DESC` are fixed; period sorts are dynamic and live
 * in the form `PERIOD_<index>_ASC` / `PERIOD_<index>_DESC`. Use the
 * {@link periodSortAsc} / {@link periodSortDesc} builders to construct them
 * so callers do not assemble the strings inline.
 *
 * The export contract still accepts a `string` over the wire so this type
 * is a string union and round-trips through the URL unchanged.
 */
export type ExpenseByVendorSort =
    | "VENDOR_ASC"
    | "VENDOR_DESC"
    | `PERIOD_${number}_ASC`
    | `PERIOD_${number}_DESC`;

export const ExpenseByVendorSort = {
    VENDOR_ASC: "VENDOR_ASC",
    VENDOR_DESC: "VENDOR_DESC",
} as const satisfies Record<string, ExpenseByVendorSort>;

const PERIOD_SORT_REGEX = /^PERIOD_(\d+)_(ASC|DESC)$/;

export const periodSortAsc = (index: number): ExpenseByVendorSort =>
    `PERIOD_${index}_ASC`;

export const periodSortDesc = (index: number): ExpenseByVendorSort =>
    `PERIOD_${index}_DESC`;

/**
 * Parse a wire-format sort token (the contract still accepts a plain string)
 * into an `ExpenseByVendorSort`.  Returns `undefined` for unknown tokens so
 * callers can fall back to default ordering instead of throwing.
 */
export function parseExpenseByVendorSort(
    value: string | undefined,
): ExpenseByVendorSort | undefined {
    if (value === undefined) {
        return undefined;
    }
    if (value === "VENDOR_ASC" || value === "VENDOR_DESC") {
        return value;
    }
    const match = PERIOD_SORT_REGEX.exec(value);
    if (match) {
        const index = Number(match[1]);
        return match[2] === "ASC"
            ? periodSortAsc(index)
            : periodSortDesc(index);
    }
    return undefined;
}

export function sortExpenseByVendorRows(
    rows: ExpenseByVendorRow[],
    sort: ExpenseByVendorSort,
): ExpenseByVendorRow[] {
    return [...rows].sort((a, b) => {
        if (a.vendorId === null) {
            return 1;
        }
        if (b.vendorId === null) {
            return -1;
        }

        if (
            sort === ExpenseByVendorSort.VENDOR_ASC ||
            sort === ExpenseByVendorSort.VENDOR_DESC
        ) {
            const cmp = a.vendorName.localeCompare(b.vendorName);
            return sort === ExpenseByVendorSort.VENDOR_ASC ? cmp : -cmp;
        }

        const match = PERIOD_SORT_REGEX.exec(sort);
        if (match) {
            const index = Number(match[1]);
            const diff = (a.values[index] ?? 0) - (b.values[index] ?? 0);
            return match[2] === "ASC" ? diff : -diff;
        }

        return 0;
    });
}
